import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  countryName, DEVICE_WORDS, FILTER_KEYS, filtered, filterWords, pageName, touchRate,
  type SiteFilter, type SiteIdea, type SiteResponse, type SiteSummary,
} from "@joogoos/contracts";
import type { AnalyticsResponse } from "@joogoos/contracts";
import { api } from "../api.js";
import { Icon } from "../ui/icons.js";
import { useNarrow } from "../ui/narrow.js";
import { HEADLINE, KICKER, Tab, TabBar, UNDER } from "./chrome.js";

const WINDOWS: Array<[number, string]> = [[7, "7 days"], [30, "30 days"], [90, "90 days"]];

/**
 * Analytics, redesigned as one screen (2026-09-20, the owner: "I need to scroll down, so a lot of lost space, not
 * very pro").
 *
 * The old tab was a column of equal boxes — five cards saying "not connected", four tiles, a chart, and only then
 * the sentence the whole tab exists for, below the fold on a laptop. This one is a page, not a dashboard: one
 * headline in display type on the left, the two lists that can be pressed on the right, and everything not counted
 * in one quiet line at the foot. On a laptop it fits without scrolling; on a phone the columns become one.
 *
 * **The headline is the strongest true thing.** Most days there is no finding — a small site counts a handful of
 * people a day and nobody rings for weeks — so the screen is designed for that first and the rich case second:
 *
 * | What is true                        | Headline                                   | Beside it              |
 * |-------------------------------------|--------------------------------------------|------------------------|
 * | no website given                    | the invitation, and the box                | —                      |
 * | line to paste, no visit yet         | "Put this in bakery.co.uk."                | what will be here      |
 * | counting, nobody in the window      | "Counting bakery.co.uk since 20 Sept."     | what will be here      |
 * | a few people, nothing to suggest    | "3 people came to bakery.co.uk this week." | the two lists          |
 * | a finding (`ideasFrom`)             | the finding, with the one coral button     | the two lists          |
 * | a filter that matches nobody        | "None of the people … saw …"               | —                      |
 *
 * Only a finding gets the coral kicker and the coral button: the colour is the product saying *this one*, and it
 * says it once.
 *
 * **Under all of it, since 2026-09-22, the four breakdowns** — where they were, what they were on, campaigns, which
 * website (`Also`). They were being recorded two days before anything read them (ADR 0035), and they go below the
 * fold rather than beside the headline: the tab is for what to make next, and a breakdown is what somebody scrolls
 * to when they have a question of their own.
 */
export function Analytics({ onMake, onBusiness }: {
  onMake: (projectId: string) => void;
  /** Where every connection is made, including this one (`Connections.tsx`). */
  onBusiness?: (() => void) | undefined;
}) {
  const [days, setDays] = useState(30);
  const [filter, setFilter] = useState<SiteFilter>({});
  // What the networks said at the last nightly read, and which of the six they are (25).
  const a = useQuery({ queryKey: ["analytics", days], queryFn: () => api.analytics(days) });
  const q = useQuery({
    // Every part of the filter, or a country pressed would be answered out of the cache of the country before it.
    queryKey: ["site", days, ...FILTER_KEYS.map((k) => filter[k] ?? null)],
    queryFn: () => api.site(days, filter),
    placeholderData: (prev) => prev,
    refetchInterval: (query) => (query.state.data && !query.state.data.connected ? 15_000 : false),
    refetchOnWindowFocus: true,
  });
  const s = q.data;
  const counting = !!(s && s.domain && s.connected && s.summary);

  return (
    <div className="h-full flex flex-col min-h-0">
      <TabBar title="What worked">
        {WINDOWS.map(([d, label]) => <Tab key={d} on={days === d} onClick={() => setDays(d)}>{label}</Tab>)}
      </TabBar>
      <div className="flex-1 min-h-0 overflow-y-auto px-4 md:px-6 py-4 md:py-5 flex flex-col">
        {!s ? null
          : !s.connected || !s.summary
            ? <NotCounting site={s} onBusiness={onBusiness} />
            : <Counted site={s} summary={s.summary} days={days} filter={filter} onFilter={setFilter} onMake={onMake} busy={q.isFetching} />}
        {a.data?.available ? <Networks a={a.data} /> : null}
        {s ? <Elsewhere a={a.data} site={s} counting={counting} /> : null}
      </div>
    </div>
  );
}

/* ── The counted screen ──────────────────────────────────────────────────────────────────────────────────────── */

function Counted({ site, summary, days, filter, onFilter, onMake, busy }: {
  site: SiteResponse; summary: SiteSummary; days: number; filter: SiteFilter; onFilter: (f: SiteFilter) => void;
  onMake: (projectId: string) => void; busy: boolean;
}) {
  const narrow = useNarrow();
  /** A row pressed twice is a row let go of. */
  const toggle = (part: SiteFilter) => onFilter({
    ...filter,
    ...Object.fromEntries(Object.entries(part).map(([k, v]) => [k, filter[k as keyof SiteFilter] === v ? null : v])),
  });
  const nobody = summary.visitors === 0;
  const idea = site.ideas[0];
  const pages = [...summary.pages].sort((x, y) => y.visitors - x.visitors);

  return (
    <section className={`grid gap-y-6 transition-opacity ${busy ? "opacity-70" : ""} ${narrow ? "grid-cols-1" : "grid-cols-[minmax(0,7fr)_minmax(0,5fr)] gap-x-10"}`}>
      <div className="min-w-0 flex flex-col gap-5">
        {/* The chips come from the answer, not from what was clicked: the headline is about exactly these people. */}
        {filtered(site.filter) ? (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[12px] text-muted">Only the people who</span>
            {filterWords(site.filter).map((w) => (
              <Chip key={w.key} label={w.label} onClear={() => onFilter({ ...filter, [w.key]: null })} />
            ))}
            <button onClick={() => onFilter({})} className="text-[12px] text-muted underline underline-offset-2 hover:text-ink-2">Show everyone</button>
          </div>
        ) : null}
        {nobody && filtered(site.filter) ? <NobodyMatches site={site} onFilter={onFilter} />
          : nobody ? <Quiet site={site} summary={summary} days={days} />
          : idea ? (
            <>
              <Finding idea={idea} rest={site.ideas.slice(1)} summary={summary} days={days} filter={site.filter} onMake={onMake} />
              <Numbers site={site} summary={summary} days={days} />
            </>
          ) : <Count site={site} summary={summary} days={days} />}
      </div>

      <div className="min-w-0 flex flex-col gap-4">
        {nobody ? (filtered(site.filter) ? null : <WillBeHere />) : (
          <>
            <Rows
              title="Who sent them"
              rows={summary.sources.map((r) => ({ key: r.name, label: r.name, n: r.visitors, got: r.enquiries, on: site.filter.source === r.name, pick: () => toggle({ source: r.name }) }))}
              empty="Nobody who said where they came from — so far everyone typed the address, or followed a link from an app."
            />
            <Rows
              title="What they came for"
              rows={pages.map((p) => ({ key: p.path, label: pageName(p.path), n: p.visitors, got: p.enquiries, on: site.filter.path === p.path, pick: () => toggle({ path: p.path }) }))}
              empty="Nothing yet."
            />
            {summary.posts.length ? (
              <Rows title="Your posts" rows={summary.posts.map((p) => ({ key: p.tag, label: p.tag, n: p.visitors, got: p.enquiries }))} empty="" />
            ) : null}
            {/* Letters by the kind of customer they were written for, never by who clicked (ADR 0035, 2026-10-21). */}
            {summary.letters.length ? (
              <Rows title="Your letters" rows={summary.letters.map((l) => ({ key: l.group, label: l.group, n: l.visitors, got: l.enquiries }))} empty="" />
            ) : null}
            <p className="text-[11.5px] text-faint leading-relaxed">Press a row to ask about those people only — the sentence at the top is rewritten for them.</p>
          </>
        )}
      </div>

      {nobody ? null : <Also summary={summary} filter={site.filter} toggle={toggle} narrow={narrow} />}
    </section>
  );
}

/**
 * The four things that were written down on 2026-09-20 and read by nothing until 2026-09-22 (ADR 0035): where they
 * were, what they read it on, which tagged link brought them, and which website really did.
 *
 * **Under the fold on purpose.** What to make next keeps the first screen, because that is what the tab is for; a
 * breakdown is what somebody scrolls to when they have a question, and the answer to "how many are on a phone" is
 * worth nothing if it costs the headline its place (the redesign of 2026-09-20). Across the full width rather than
 * down the right column, because four lists stacked in a narrow column is the old tab's scroll coming back.
 *
 * A country and a device can be pressed, because they are on every row a person leaves and mean the same as every
 * other filter here: *these people, and everything they did*. A campaign and a host are read-only for now — they
 * are on the arrival only, and the source beside them already filters the same set of people.
 */
function Also({ summary, filter, toggle, narrow }: {
  summary: SiteSummary; filter: SiteResponse["filter"]; toggle: (part: SiteFilter) => void; narrow: boolean;
}) {
  const { countries, devices, campaigns, referrers } = summary;
  if (!countries.length && !devices.length && !campaigns.length && !referrers.length) return null;
  return (
    <div className={`border-t border-line pt-5 ${narrow ? "" : "col-span-2"}`}>
      <div className={`grid gap-x-8 gap-y-5 ${narrow ? "grid-cols-1" : "grid-cols-2 lg:grid-cols-4"}`}>
        <Rows
          title="Where they were"
          rows={countries.map((c) => ({
            key: c.code, label: countryName(c.code), n: c.visitors, got: c.enquiries,
            on: filter.country === c.code, pick: () => toggle({ country: c.code }),
          }))}
          empty="Not known for anybody in this window."
        />
        <Rows
          title="What they were on"
          rows={devices.map((d) => ({
            key: d.device, label: DEVICE_WORDS[d.device] ?? d.device, n: d.visitors, got: d.enquiries,
            on: filter.device === d.device, pick: () => toggle({ device: d.device }),
          }))}
          empty="Not known for anybody in this window."
        />
        <Rows
          title="Campaigns"
          rows={campaigns.map((c) => ({ key: c.name, label: c.name, n: c.visitors, got: c.enquiries }))}
          empty="Nothing tagged. A link ending ?utm_campaign=… is counted here, so an ad can be told from the rest."
        />
        <Rows
          title="Which website"
          rows={referrers.map((r) => ({ key: r.host, label: r.host, n: r.visitors, got: r.enquiries }))}
          empty="Nobody arrived from another website."
        />
      </div>
      <p className="mt-4 text-[11.5px] text-faint leading-relaxed max-w-[80ch]">
        These come from the visit itself and are only ever counted, never shown beside a person: a country is a
        country, and a phone is one of a million phones. Visits counted before 20 September have none of them.
      </p>
    </div>
  );
}

/** The one headline style, so every state of this screen is recognisably the same page. */
/**
 * A finding: the point of the tab, at the top, in display type, with the one coral button. The other findings are
 * there, quieter — three coral buttons of equal weight was the old card's way of saying nothing mattered more than
 * anything else.
 */
function Finding({ idea, rest, summary, days, filter, onMake }: {
  idea: SiteIdea; rest: SiteIdea[]; summary: SiteSummary; days: number; filter: SiteResponse["filter"]; onMake: (projectId: string) => void;
}) {
  const qc = useQueryClient();
  const make = useMutation({
    mutationFn: async (text: string) => {
      const project = await api.createProject();
      await api.answer(project.id, { idea: text });
      return project.id;
    },
    onSuccess: (id) => { void qc.invalidateQueries({ queryKey: ["projects"] }); onMake(id); },
  });
  const why = because(idea, summary, days, filter);
  return (
    <div>
      <h2 className={`${KICKER} text-coral`}>What to make next</h2>
      <p className={HEADLINE}>{idea.text}</p>
      {why ? <p className={UNDER}>{why}</p> : null}
      <button
        onClick={() => make.mutate(idea.idea)}
        disabled={make.isPending}
        className="mt-3 inline-flex items-center gap-1.5 bg-coral text-white rounded-lg px-3.5 py-2 text-[13px] font-semibold disabled:opacity-50"
      >
        <Icon name="wand" size={14} /> Make this
      </button>
      {rest.length ? (
        <ul className="mt-4 pt-3 border-t border-line flex flex-col gap-1.5">
          {rest.map((r) => (
            <li key={r.text} className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-0.5 text-[13.5px]">
              <span>{r.text}</span>
              <button onClick={() => make.mutate(r.idea)} disabled={make.isPending} className="shrink-0 text-coral font-semibold hover:underline underline-offset-2 disabled:opacity-50">
                Make this
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {make.error ? <p className="text-[12.5px] text-coral mt-2">{String(make.error)}</p> : null}
    </div>
  );
}

/**
 * The figure the finding was made from, said back so the sentence is not taken on trust. The rules that write the
 * ideas (contracts/site.ts) put a page or a source in every sentence, so the one it was about can be found again.
 */
function because(idea: SiteIdea, s: SiteSummary, days: number, filter: SiteResponse["filter"]): string | undefined {
  const home = s.pages.find((p) => p.path === "/");
  const page = s.pages.find((p) => p.path !== "/" && idea.text.includes(pageName(p.path))) ?? (idea.text.includes("home page") ? home : undefined);
  if (page) {
    if (page.enquiries) {
      const who = !filter.source ? s.sources.filter((x) => x.enquiries > 0).sort((x, y) => y.enquiries - x.enquiries)[0] : undefined;
      return `${page.visitors} ${page.visitors === 1 ? "person" : "people"} saw ${pageName(page.path)} in the last ${days} days, and ${page.enquiries === page.visitors ? "every one of them" : `${page.enquiries} of them`} got in touch.${who ? ` ${who.name} sent ${who.enquiries === s.enquiries ? "everyone who did" : `${who.enquiries} of the ${s.enquiries} who did`}.` : ""}`;
    }
    if (page.path === "/") return `${page.visitors === s.visitors ? `All ${s.visitors}` : `${page.visitors} of the ${s.visitors}`} stopped at the front door — the site is not saying what you sell.`;
    return `${page.visitors} of the ${s.visitors} people saw ${pageName(page.path)} — more than any other page${home && home.visitors > page.visitors ? " after your home page" : ""}.`;
  }
  const source = s.sources.find((x) => idea.text.includes(x.name));
  if (source) {
    return `${source.name} sent ${source.visitors} of the ${s.visitors} people in the last ${days} days${source.enquiries ? `, and ${source.enquiries} of them got in touch` : ", and none of them has got in touch yet"}.`;
  }
  return undefined;
}

/**
 * A few people and nothing to suggest — the ordinary state of a small site for weeks, and the one this screen is
 * looked at in most. The count is the headline, because it is the news; the facts under it are the same ones a
 * finding would be made of; and what a finding needs is said in one muted line, so the empty place where the coral
 * button would be is an explanation rather than an absence.
 */
function Count({ site, summary, days }: { site: SiteResponse; summary: SiteSummary; days: number }) {
  return (
    <div>
      <h2 className={`${KICKER} text-muted`}>So far</h2>
      <p className={HEADLINE}>{people(summary.visitors)} {came(site)} in the last {days} days.</p>
      <p className={UNDER}>{facts(summary, days, site.filter)}</p>
      <Spark series={summary.series} />
      <p className="text-[12.5px] text-muted mt-4 pt-3 border-t border-line leading-relaxed max-w-[60ch]">
        <b className="font-semibold text-ink-2">What to make next</b> appears here once three or so people have looked at the same page, or somebody
        gets in touch — one sentence, and a button that opens the Studio on it.
      </p>
    </div>
  );
}

/**
 * Counting, and nobody in the window. On the day the line goes in — which is every site's first day, and for a
 * small site most of its first week — the largest thing on the screen is proof that it works. Not "no data": the
 * script is in, the first visit landed, and the screen is waiting with the customer rather than at them.
 */
function Quiet({ site, summary, days }: { site: SiteResponse; summary: SiteSummary; days: number }) {
  const since = site.installedAt ? new Date(site.installedAt) : null;
  const recent = since ? Date.now() - since.getTime() < days * 86_400_000 : false;
  return (
    <div>
      <h2 className={`${KICKER} text-muted`}>Your website</h2>
      <p className={HEADLINE}>
        {recent && since ? <>Counting {site.domain} since {short(since.toISOString())}.</> : <>Nobody came to {site.domain} in the last {days} days.</>}
      </p>
      <p className={UNDER}>
        {recent
          ? "The line is in and working. The first person to arrive appears here by themselves, and a sentence about what to make follows after a handful of them. Nothing for you to do."
          : "The line is still in and still counting: the next person to arrive appears here by themselves. Try a longer window above, or a post with a link to the site."}
      </p>
      {summary.live ? (
        <p className="inline-flex items-center gap-1.5 mt-3 text-[13px] text-good font-medium">
          <span className="w-1.5 h-1.5 rounded-full bg-good animate-pulse" />{summary.live} here now
        </p>
      ) : null}
    </div>
  );
}

/** Two rows pressed that nobody satisfies. The way out is the largest thing, and it is ink, not coral. */
function NobodyMatches({ site, onFilter }: { site: SiteResponse; onFilter: (f: SiteFilter) => void }) {
  const { source, path } = site.filter;
  return (
    <div>
      <h2 className={`${KICKER} text-muted`}>Nobody to count</h2>
      <p className={HEADLINE}>
        {source && path ? <>None of the people {source} sent saw {pageName(path)}.</>
          : source ? <>Nobody came from {source} in the last {site.days} days.</>
          : <>Nobody saw {path ? pageName(path) : "that page"} in the last {site.days} days.</>}
      </p>
      <p className={UNDER}>{source && path ? "Let go of one of the two, or show everyone." : "Try a longer window above, or show everyone."}</p>
      <button onClick={() => onFilter({})} className="mt-3 inline-flex items-center gap-1.5 bg-ink text-white rounded-lg px-3.5 py-2 text-[13px] font-semibold">
        Show everyone
      </button>
    </div>
  );
}

/**
 * Four tiles turned into one sentence and a sparkline. "96 people, 22 more than last month, 2 got in touch" is
 * what the tiles said with a whole band of the screen; a sentence says it in the room a line takes, and can carry
 * "more than" and "nobody" where a tile can only carry a figure.
 */
function Numbers({ site, summary, days }: { site: SiteResponse; summary: SiteSummary; days: number }) {
  const d = summary.before ? summary.visitors - summary.before.visitors : null;
  return (
    <div className="border-t border-line pt-4">
      <p className="text-[13.5px] leading-relaxed">
        <b className="font-bold tabular-nums">{people(summary.visitors)}</b> {came(site)} in the last {days} days
        {d === null ? "" : d === 0 ? `, the same as the ${days} days before` : `, ${round(Math.abs(d))} ${d > 0 ? "more" : "fewer"} than the ${days} days before`}
        {summary.views > summary.visitors ? `, reading ${round(summary.views)} pages between them` : ""}.{" "}
        {touched(summary, site.filter)}
        {summary.fromPosts ? ` ${summary.fromPosts} ${summary.fromPosts === 1 ? "was" : "were"} sent by your posts.` : ""}
        {summary.fromLetters ? ` ${summary.fromLetters} ${summary.fromLetters === 1 ? "was" : "were"} sent by your letters.` : ""}
        {/* "Now" is never filtered (contracts/site.ts), so it is only said when the sentence is about everyone. */}
        {!filtered(site.filter) && summary.live ? (
          <span className="inline-flex items-center gap-1.5 ml-2 text-good font-medium whitespace-nowrap">
            <span className="w-1.5 h-1.5 rounded-full bg-good animate-pulse" />{summary.live} here now
          </span>
        ) : null}
      </p>
      <Spark series={summary.series} />
    </div>
  );
}

/** The facts under a count that is not yet a finding: the busiest source and page, the change, and the phone. */
function facts(s: SiteSummary, days: number, filter: SiteResponse["filter"]): string {
  const out: string[] = [];
  const src = filter.source ? undefined : s.sources[0];
  if (src) out.push(`${src.name} sent ${src.visitors === s.visitors ? "all of them" : src.visitors === 1 ? "one of them" : `${src.visitors} of them`}.`);
  const page = filter.path ? undefined : [...s.pages].filter((p) => p.path !== "/").sort((a, b) => b.visitors - a.visitors)[0];
  if (page) out.push(`${page.visitors === 1 ? "One" : page.visitors} looked at ${pageName(page.path)}.`);
  const d = s.before ? s.visitors - s.before.visitors : null;
  if (d !== null && d !== 0) out.push(`${Math.abs(d)} ${d > 0 ? "more" : "fewer"} than the ${days} days before.`);
  out.push(touched(s, filter));
  return out.join(" ");
}

/**
 * Whether anybody got in touch, and — while nobody has — what would count, said once. "Nobody got in touch" on its
 * own reads as a fault in the counting; the true reading is that the phone number has not been pressed yet.
 */
function touched(s: SiteSummary, filter: SiteResponse["filter"]): string {
  if (s.enquiries === 0) return filtered(filter) ? "None of them got in touch." : "Nobody has got in touch yet — pressing your phone number, email or WhatsApp link counts by itself, and a form or button once your website marks it (Business, under Website & analytics).";
  const rate = touchRate(s)?.replace(" got in touch", "");
  return `${s.enquiries === 1 ? "One" : s.enquiries} got in touch${rate ? ` (${rate})` : ""}.`;
}

/** What the right column holds once there is anybody to list — said in words while there is not. */
function WillBeHere() {
  return (
    <div className="text-[12.5px] text-muted leading-relaxed">
      <h3 className={`${KICKER} text-muted`}>What will be here</h3>
      <ul className="mt-2 flex flex-col gap-2">
        <li><b className="font-semibold text-ink-2">Who sent them</b> — Instagram, Google, another website, and how many each.</li>
        <li><b className="font-semibold text-ink-2">What they came for</b> — each page, and how many read it.</li>
        <li><b className="font-semibold text-ink-2">Who got in touch</b> — a press on your phone number, email or WhatsApp link, against the page and the place it came from.</li>
      </ul>
      <p className="mt-3 text-faint">Any of them can be pressed to ask about those people only.</p>
    </div>
  );
}

/** People a day as a shape, not a chart: no axes, the days somebody rang marked on the baseline, the busiest day named. */
function Spark({ series }: { series: SiteSummary["series"] }) {
  const [at, setAt] = useState<number | null>(null);
  const narrow = useNarrow();
  const W = narrow ? 340 : 640, H = narrow ? 44 : 64, P = 4;
  const w = W - 2 * P, h = H - P - 6;
  const max = Math.max(4, ...series.map((p) => p.visitors));
  const x = (i: number) => P + (series.length < 2 ? w / 2 : (i / (series.length - 1)) * w);
  const y = (v: number) => P + h - (v / max) * h;
  const line = series.map((p, i) => `${i ? "L" : "M"}${x(i).toFixed(1)} ${y(p.visitors).toFixed(1)}`).join(" ");
  const area = series.length ? `${line} L${x(series.length - 1).toFixed(1)} ${P + h} L${x(0).toFixed(1)} ${P + h} Z` : "";
  const hovered = at === null ? null : series[at];
  const busiest = series.reduce<SiteSummary["series"][number] | null>((b, p) => (p.visitors > (b?.visitors ?? 0) ? p : b), null);
  const first = series[0], last = series.at(-1);
  const rang = series.some((p) => p.enquiries);

  return (
    <figure className="mt-3">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full block" role="img" aria-label={`People a day over ${series.length} days, up to ${max}`}>
        <defs>
          <linearGradient id="spark-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--color-coral)" stopOpacity="0.18" />
            <stop offset="100%" stopColor="var(--color-coral)" stopOpacity="0.02" />
          </linearGradient>
        </defs>
        <line x1={P} y1={P + h} x2={W - P} y2={P + h} stroke="var(--color-line)" strokeWidth="1" />
        {area ? <path d={area} fill="url(#spark-fill)" /> : null}
        {line ? <path d={line} fill="none" stroke="var(--color-coral)" strokeWidth="1.75" strokeLinejoin="round" strokeLinecap="round" /> : null}
        {series.map((p, i) => (p.enquiries ? <circle key={p.date} cx={x(i)} cy={P + h} r="3" fill="var(--color-ink)" stroke="var(--color-panel)" strokeWidth="1.5" /> : null))}
        {hovered ? (
          <g>
            <line x1={x(at!)} y1={P} x2={x(at!)} y2={P + h} stroke="var(--color-ink)" strokeWidth="1" strokeDasharray="2 2" />
            <circle cx={x(at!)} cy={y(hovered.visitors)} r="3.5" fill="var(--color-coral)" stroke="var(--color-panel)" strokeWidth="1.5" />
          </g>
        ) : null}
        <rect
          x={0} y={0} width={W} height={H} fill="transparent"
          onMouseLeave={() => setAt(null)}
          onMouseMove={(e) => {
            const r = e.currentTarget.getBoundingClientRect();
            const i = Math.round(((e.clientX - r.left) / r.width) * (series.length - 1));
            setAt(Math.max(0, Math.min(series.length - 1, i)));
          }}
        />
      </svg>
      <figcaption className="flex justify-between gap-3 text-[11px] text-faint tabular-nums mt-1">
        <span className="whitespace-nowrap">{first ? short(first.date) : ""}</span>
        <span className="text-muted truncate min-w-0">
          {hovered
            ? `${short(hovered.date)}: ${few(hovered.visitors)}${hovered.enquiries ? `, ${hovered.enquiries} got in touch` : ""}`
            : busiest?.visitors ? `busiest day ${short(busiest.date)}, ${few(busiest.visitors)}${narrow || !rang ? "" : " · ● somebody got in touch"}` : "a day a person"}
        </span>
        <span className="whitespace-nowrap">{last ? short(last.date) : ""}</span>
      </figcaption>
    </figure>
  );
}

interface Row { key: string; label: string; n: number; got?: number | undefined; on?: boolean | undefined; pick?: (() => void) | undefined }

/** A pressable ranked list, without a card round it: the bar behind each row is all the framing it needs. */
function Rows({ title, rows, empty }: { title: string; rows: Row[]; empty: string }) {
  const max = Math.max(1, ...rows.map((r) => r.n));
  return (
    <div>
      <h3 className={`${KICKER} text-muted`}>{title}</h3>
      {rows.length ? (
        <ul className="mt-1.5 flex flex-col gap-px">
          {rows.slice(0, 5).map((r) => {
            const inside = (
              <>
                <span aria-hidden className={`absolute inset-y-0 left-0 rounded-[5px] ${r.on ? "bg-coral/25" : "bg-coral/12"}`} style={{ width: `${Math.max(2, (r.n / max) * 100)}%` }} />
                <span className={`relative truncate ${r.on ? "font-semibold" : ""}`}>{r.label}</span>
                <span className="relative shrink-0 tabular-nums text-[12.5px]">
                  {r.got ? <span className="text-coral font-medium mr-2.5">{r.got} in touch</span> : null}
                  <span className="text-ink-2">{round(r.n)}</span>
                </span>
              </>
            );
            const shape = "relative flex items-center justify-between gap-3 w-full text-left text-[13px] px-2 py-1.5 rounded-[5px] overflow-hidden";
            return (
              <li key={r.key}>
                {r.pick
                  ? <button onClick={r.pick} aria-pressed={!!r.on} className={`${shape} hover:ring-1 hover:ring-line-2 ${r.on ? "ring-1 ring-ink" : ""}`}>{inside}</button>
                  : <span className={shape}>{inside}</span>}
              </li>
            );
          })}
        </ul>
      ) : <p className="text-[12.5px] text-muted mt-1 leading-relaxed">{empty}</p>}
    </div>
  );
}

function Chip({ label, onClear }: { label: string; onClear: () => void }) {
  return (
    <span className="flex items-center gap-1.5 bg-ink text-white rounded-full pl-2.5 pr-1.5 py-1 text-[12px] font-medium">
      {label}
      <button onClick={onClear} aria-label={`Stop showing only the people who ${label}`} className="opacity-70 hover:opacity-100"><Icon name="x" size={12} /></button>
    </span>
  );
}

const count = (n: number | null) => (n === null ? "—" : n.toLocaleString("en"));

/**
 * The networks' half, read each night (25). Under the website's half, because the tab is for what to make next and these
 * numbers are the answer to a question of their own. A figure a network does not give is a dash, never a zero.
 */
function Networks({ a }: { a: AnalyticsResponse }) {
  const standing = a.channels.filter((c) => c.followers !== null);
  return (
    <section className="mt-6 flex flex-col gap-3">
      <h2 className="text-[13px] font-semibold text-ink">What your posts did</h2>
      {standing.length ? (
        <p className="text-[12.5px] text-ink-2">
          {standing.map((c) => `${c.channel} ${count(c.followers)} followers${c.delta ? ` (${c.delta > 0 ? "+" : ""}${count(c.delta)})` : ""}`).join(" · ")}
        </p>
      ) : null}
      {a.posts.length ? (
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px] text-ink-2">
            <thead><tr className="text-left text-[11.5px] text-muted"><th className="font-medium py-1">Post</th><th className="font-medium">Where</th><th className="font-medium text-right">Views</th><th className="font-medium text-right">Likes</th><th className="font-medium text-right">Comments</th></tr></thead>
            <tbody>
              {a.posts.map((p) => (
                <tr key={p.id} className="border-t border-line">
                  <td className="py-1.5 pr-3 max-w-[260px] truncate">{p.url ? <a href={p.url} target="_blank" rel="noreferrer" className="hover:underline">{p.title}</a> : p.title}</td>
                  <td className="pr-3">{p.channel}</td>
                  <td className="text-right">{count(p.views)}</td>
                  <td className="text-right">{count(p.likes)}</td>
                  <td className="text-right">{count(p.comments)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}

/**
 * **This is the sources strip, folded.** While a place cannot be counted it is one line at the foot, in words, naming
 * the same places (`CHANNELS` in contracts/tabs.ts, plus reviews): on 2026-09-20 five dashed boxes spent a third of the
 * first screen on what the product cannot do.
 */
function Elsewhere({ a, site, counting }: { a: AnalyticsResponse | undefined; site: SiteResponse; counting: boolean }) {
  const waiting = [...(a?.reviews ? [] : ["Google reviews"]), ...(a?.channels ?? []).filter((c) => c.followers === null).map((c) => c.channel)];
  const idle = counting && site.summary?.fromPosts === 0 && site.summary.visitors > 0;
  return (
    <p className="mt-auto pt-6 text-[12px] text-muted leading-relaxed">
      {counting ? <>Counting <b className="font-medium text-ink-2">{site.domain}</b>, and only that. </> : null}
      {waiting.length ? (
        <>
          <b className="font-medium text-ink-2">{list(waiting)}</b>: not counted here yet. Accounts are connected on the Business page, and
          a network Joogoos can read is read each night.
        </>
      ) : null}
      {idle ? " No post has sent anyone to the website yet — links carry a post's name once Joogoos publishes for you." : ""}
    </p>
  );
}

/* ── Before the numbers: which website, and the line to paste ─────────────────────────────────────────────────── */

/** The first screen of a new workspace. The largest thing is the invitation, and the box is already filled in where it can be. */
/**
 * Before anything is counted, this tab points and does not ask (2026-09-20).
 *
 * Setting a website up is a connection, and every connection a business has — its website, its Google listing, the
 * six accounts posts go out on — is made in one place, on the Business page (`Connections.tsx`). A tab of numbers
 * carrying a form was the old shape, and it meant the page about connections did not mention the one that works.
 *
 * So both states keep the headline and the column that says what will be here, and neither carries the form.
 */
function NotCounting({ site, onBusiness }: { site: SiteResponse; onBusiness?: (() => void) | undefined }) {
  const narrow = useNarrow();
  const waiting = !!site.domain;
  return (
    <section className={`grid gap-y-6 ${narrow ? "grid-cols-1" : "grid-cols-[minmax(0,7fr)_minmax(0,5fr)] gap-x-10"}`}>
      <div className="min-w-0">
        <h2 className={`${KICKER} text-muted`}>{waiting ? "One line to paste" : "Your website"}</h2>
        <p className={HEADLINE}>
          {waiting ? <>Waiting for the first visit to {site.domain}.</> : "Count who comes to your website, and what they come for."}
        </p>
        <p className={UNDER}>
          {waiting
            ? "The line is on your Business page, under Website & analytics. Paste it into your website and this fills in by itself — nothing to press here."
            : "One line in your website tells Joogoos how many people came, what they looked at and who picked up the phone, so the next post is about what people are already interested in. It is set up on your Business page, under Website & analytics."}
        </p>
        {onBusiness ? (
          <button onClick={onBusiness} className="mt-4 bg-ink text-white rounded-lg px-3.5 py-2 text-[13px] font-semibold">
            {waiting ? "Open the Business page" : "Set it up on the Business page"}
          </button>
        ) : null}
        {waiting ? (
          <p className="mt-4 flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px]">
            <span className="w-1.5 h-1.5 rounded-full bg-coral animate-pulse" />
            <span className="font-medium">Nothing has arrived yet.</span>
            <span className="text-muted">The moment somebody does, this page is the numbers instead.</span>
          </p>
        ) : null}
      </div>
      <WillBeHere />
    </section>
  );
}

/** "a, b and c" — the way a person lists things. */
const list = (all: string[]) => (all.length < 2 ? (all[0] ?? "") : `${all.slice(0, -1).join(", ")} and ${all.at(-1)}`);
const few = (n: number) => (n === 1 ? "one person" : `${round(n)} people`);
const people = (n: number) => (n === 1 ? "One person" : `${round(n)} people`);
/** "came to bakery.co.uk", or what the chips above say, in the same words and in the same order. */
const came = (site: SiteResponse) => {
  const words = filterWords(site.filter).map((w) => w.label);
  return words.length ? list(words) : `came to ${site.domain}`;
};
const round = (n: number) => (Math.abs(n) >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(Math.round(n)));
const short = (iso: string) => new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" });
